import React, { useMemo } from "react";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { getPLAAttributeUsage, PLAFormulaSource } from "./PLAAttributeUsage";
import type { PLAElementRecord, PLAProject } from "./PLAState";
import "./PLAWindow.css";

export type PLAAttributeUsageWindowParams = { project: PLAProject; elements: PLAElementRecord[] };

export function PLAAttributeUsageWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as PLAAttributeUsageWindowParams | undefined;
   const overview = useMemo(() => params ? getPLAAttributeUsage(params.project, params.elements) : undefined, [params]);
   if (!params || !overview) return null;
   const { project } = params;
   const typeName = (id: string) => project.elementTypes[id]?.name || id;
   const attributeName = (typeId: string, id: string) => project.elementTypes[typeId]?.attributes[id]?.name || id;
   const sourceLabel = (source: PLAFormulaSource) => [
      Localization.getText(source.elementId !== undefined ? "Element formula" : source.alertId !== undefined ? "Alert rule" : "Project formula"),
      typeName(source.elementTypeId),
      attributeName(source.elementTypeId, source.attributeId),
      ...(source.elementId !== undefined ? [source.elementName || source.elementId] : []),
      ...(source.alertId !== undefined ? [source.alertId] : [])
   ].join(" / ");
   const groups = Object.entries(project.elementTypes).sort(([, a], [, b]) => a.sortOrder - b.sortOrder).flatMap(([elementTypeId, elementType]) => {
      const attributes = getSortedPLAAttributeEntries(elementType.attributes).flatMap(([attributeId]) => {
         const references = overview.usage.filter((entry) => entry.elementTypeId === elementTypeId && entry.attributeId === attributeId);
         return references.length ? [{ attributeId, references }] : [];
      });
      return attributes.length ? [{ elementTypeId, attributes }] : [];
   });
   const close = () => dispatch(closeWindow(props.managedWindow.id));

   return (
      <AdvancedDragModal
         title={`${Localization.getText("Attribute usage")} - ${project.name}`}
         window={props.managedWindow}
         allowUserMiniMize={true}
         PosDefault={{ width: "1100px", height: "620px", left: "50%", transform: "translateX(-50%)", top: "80px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
         saveKey="PLAAttributeUsage"
         onClose={close}
      >
         <ADModalBody>
            <div className="pla-configured-alerts">
               <p>{Localization.getText("PLA attribute usage explanation")}</p>
               <table>
                  <thead><tr>
                     <th>{Localization.getText("Element type")}</th>
                     <th>{Localization.getText("Attribute")}</th>
                     <th>{Localization.getText("Defined in")}</th>
                     <th>{Localization.getText("Expression")}</th>
                     <th>{Localization.getText("Reference")}</th>
                  </tr></thead>
                  <tbody>
                     {groups.flatMap((group) => {
                        let firstType = true;
                        const rowCount = group.attributes.reduce((total, attribute) => total + attribute.references.length, 0);
                        return group.attributes.flatMap((attribute) => attribute.references.map((entry, index) => {
                           const showType = firstType;
                           firstType = false;
                           return <tr key={JSON.stringify([group.elementTypeId, attribute.attributeId, index])}>
                              {showType ? <th scope="rowgroup" rowSpan={rowCount}>{typeName(group.elementTypeId)}</th> : null}
                              {index === 0 ? <th scope="rowgroup" rowSpan={attribute.references.length}>{attributeName(group.elementTypeId, attribute.attributeId)}</th> : null}
                              <td>{sourceLabel(entry.source)}</td>
                              <td><code>{entry.source.expression}</code></td>
                              <td>{Localization.getText(entry.crossElement ? "Possible cross-element reference" : "Local reference")}</td>
                           </tr>;
                        }));
                     })}
                     {!groups.length ? <tr><td colSpan={5}>{Localization.getText("No attribute references found")}</td></tr> : null}
                  </tbody>
               </table>
               {overview.issues.length ? <>
                  <h4>{Localization.getText("Unresolved references")}</h4>
                  <table>
                     <thead><tr><th>{Localization.getText("Defined in")}</th><th>{Localization.getText("Expression")}</th><th>{Localization.getText("Message")}</th></tr></thead>
                     <tbody>{overview.issues.map((issue, index) => <tr key={index}>
                        <td>{sourceLabel(issue.source)}</td>
                        <td><code>{issue.source.expression}</code></td>
                        <td>{Localization.getText(issue.reason === "dynamic" ? "Dynamic attribute reference" : issue.reason === "unknown" ? "Unknown attribute reference" : "Expression could not be parsed")}: {issue.detail}</td>
                     </tr>)}</tbody>
                  </table>
               </> : null}
            </div>
         </ADModalBody>
         <ADModalFooter><GlassButtonRow adjust="end"><GlassButton onClick={close}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
      </AdvancedDragModal>
   );
}
